import { ComponentProps, forwardRef } from "react";
import { State } from "./input-states.ts";
import { cn } from "../../utils/cn.ts";
import {
  baseFieldStyle,
  textareaPaddingStyle,
  baseMessageStyle,
  getFieldStyleForState,
  getMessageStyleForState,
  getLabelStyle,
} from "./input-styles.ts";

export type TextareaProps = Omit<ComponentProps<"textarea">, "children"> & {
  state: State;
  label?: string;
  labelSize?: "default" | "large";
  message?: string;
  showRequiredAsterisk?: boolean;
  resize?: "none" | "vertical" | "horizontal" | "both";
};

const resizeStyles: Record<NonNullable<TextareaProps["resize"]>, string> = {
  none: "resize-none",
  vertical: "resize-y",
  horizontal: "resize-x",
  both: "resize",
};

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      state,
      id,
      placeholder,
      label,
      labelSize = "default",
      message,
      value,
      onChange,
      onBlur,
      showRequiredAsterisk = false,
      resize = "none",
      className,
      ...rest
    },
    ref,
  ) => {
    const textareaStyle = cn(
      baseFieldStyle,
      textareaPaddingStyle,
      getFieldStyleForState(state),
      resizeStyles[resize],
      className,
    );
    const messageStyle = cn(baseMessageStyle, getMessageStyleForState(state));
    const labelStyle = getLabelStyle(labelSize);
    const messageId = message && id ? `${id}-message` : undefined;

    return (
      <div className="flex flex-col w-full gap-1.5 md:gap-2">
        {label && (
          <label className={labelStyle} htmlFor={id}>
            {label}
            {showRequiredAsterisk && <span className="text-brick"> *</span>}
          </label>
        )}
        <textarea
          ref={ref}
          className={textareaStyle}
          id={id}
          placeholder={placeholder}
          disabled={state === "disabled"}
          aria-invalid={state === "error"}
          aria-describedby={messageId}
          aria-required={showRequiredAsterisk || undefined}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          {...rest}
        />
        {message && (
          <span id={messageId} className={messageStyle}>
            {message}
          </span>
        )}
      </div>
    );
  },
);

Textarea.displayName = "Textarea";
